import './SignIn.scss';

import {
  Bullseye,
  Content,
  ContentVariants,
  LoginForm,
  LoginPage,
  Spinner,
} from '@patternfly/react-core';
import { ExclamationCircleIcon } from '@patternfly/react-icons';
import { FunctionComponent, useCallback, useEffect, useState } from 'react';

import { apiBaseUrl } from './data/client';
import { onSignedOut } from './data/http';
import { changeOwnPassword, signIn, SignedInUser, whoAmI } from './data/session';

/**
 * Who is signed in, for anything that wants to know.
 *
 * Deliberately not a React context: the gate is above everything, so by the time any screen
 * renders there is always somebody, and a module-level value avoids threading a provider through
 * Kaoto's own tree for one string and a role.
 */
let current: SignedInUser | undefined;

export const signedInUser = (): SignedInUser | undefined => current;

/**
 * The shell both steps share, so the second does not look like something went wrong.
 *
 * It says as little as it can. This page is reachable by anyone who can reach the host, before
 * they have proved anything — so it carries no product logo, no description of what the platform
 * does and no list of what it can do. All of that told a stranger which software this is built on
 * and what it is for, which is the opposite of what a white-labelled product wants on its door.
 */
const Shell: FunctionComponent<{
  title: string;
  subtitle?: string;
  children: React.ReactNode;
}> = ({ title, subtitle, children }) => (
  <div className="casa-signin">
    {/* The wordmark is the heading. There is no logo file of our own, and the only one to hand
        is the upstream project's. */}
    <LoginPage loginTitle="Casa" loginSubtitle={subtitle ?? title}>
      {children}
    </LoginPage>
  </div>
);

const SignInForm: FunctionComponent<{ onSignedIn: (user: SignedInUser) => void }> = ({ onSignedIn }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const submit = useCallback(
    (event?: React.FormEvent | React.MouseEvent) => {
      event?.preventDefault();
      if (!email.trim() || !password || busy) return;
      setBusy(true);
      setError(undefined);
      signIn(email, password)
        .then(onSignedIn)
        .catch((cause: Error) => {
          setError(cause.message);
        })
        .finally(() => {
          setBusy(false);
        });
    },
    [email, password, busy, onSignedIn],
  );

  return (
    <Shell title="Sign in" subtitle="Sign in to continue.">
      <LoginForm
        data-testid="sign-in-form"
        usernameLabel="Email"
        usernameValue={email}
        onChangeUsername={(_event, value) => {
          setEmail(value);
        }}
        isValidUsername={!error}
        passwordLabel="Password"
        passwordValue={password}
        onChangePassword={(_event, value) => {
          setPassword(value);
        }}
        isValidPassword={!error}
        isShowPasswordEnabled
        showPasswordAriaLabel="Show the password"
        hidePasswordAriaLabel="Hide the password"
        loginButtonLabel={busy ? 'Signing in…' : 'Sign in'}
        isLoginButtonDisabled={busy || !email.trim() || !password}
        onLoginButtonClick={submit}
        onSubmit={submit}
        showHelperText={Boolean(error)}
        helperText={<span data-testid="sign-in-error">{error}</span>}
        helperTextIcon={<ExclamationCircleIcon />}
      />
    </Shell>
  );
};

/**
 * The first sign-in, where the password somebody read out is exchanged for one only you know.
 *
 * The control plane refuses everything else until this is done, so there is nowhere else to go
 * from here — which is the point of it.
 */
const ChangePasswordForm: FunctionComponent<{ onChanged: () => void }> = ({ onChanged }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [error, setError] = useState<string>();
  const [busy, setBusy] = useState(false);

  const submit = useCallback(
    (event?: React.FormEvent | React.MouseEvent) => {
      event?.preventDefault();
      if (busy) return;
      setBusy(true);
      setError(undefined);
      changeOwnPassword(currentPassword, newPassword)
        .then(onChanged)
        .catch((cause: Error) => {
          setError(cause.message);
        })
        .finally(() => {
          setBusy(false);
        });
    },
    [currentPassword, newPassword, busy, onChanged],
  );

  return (
    <Shell title="Choose your own password" subtitle="Choose a new password to continue.">
      <Content component={ContentVariants.small} className="casa-signin__note">
        You signed in with a password somebody handed you. Replace it and it stops working.
      </Content>
      <LoginForm
        data-testid="password-form"
        usernameLabel="The password you were given"
        usernameValue={currentPassword}
        onChangeUsername={(_event, value) => {
          setCurrentPassword(value);
        }}
        isValidUsername={!error}
        passwordLabel="Your new password"
        passwordValue={newPassword}
        onChangePassword={(_event, value) => {
          setNewPassword(value);
        }}
        isValidPassword={!error}
        isShowPasswordEnabled
        showPasswordAriaLabel="Show the password"
        hidePasswordAriaLabel="Hide the password"
        loginButtonLabel={busy ? 'Saving…' : 'Save and continue'}
        isLoginButtonDisabled={busy || !currentPassword || !newPassword}
        onLoginButtonClick={submit}
        onSubmit={submit}
        showHelperText={Boolean(error)}
        helperText={<span data-testid="password-error">{error}</span>}
        helperTextIcon={<ExclamationCircleIcon />}
      />
    </Shell>
  );
};

/**
 * Nothing renders until somebody is signed in.
 *
 * Skipped entirely when there is no control plane to sign in to, which is the offline demo and
 * every test — the same `apiBaseUrl()` check the rest of the console already makes before it
 * reaches for the network.
 */
export const SignInGate: FunctionComponent<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<SignedInUser>();
  const [checked, setChecked] = useState(false);
  const gated = Boolean(apiBaseUrl());

  useEffect(() => {
    if (!gated) {
      setChecked(true);

      return;
    }
    void whoAmI()
      .then((found) => {
        current = found;
        setUser(found);
      })
      .finally(() => {
        setChecked(true);
      });
  }, [gated]);

  /* Any request anywhere can discover the session has ended; when one does, so does this. */
  useEffect(
    () =>
      onSignedOut(() => {
        current = undefined;
        setUser(undefined);
      }),
    [],
  );

  if (!gated) return <>{children}</>;

  if (!checked) {
    return (
      <Bullseye style={{ minHeight: '100vh' }}>
        <Spinner aria-label="Checking whether you are signed in" />
      </Bullseye>
    );
  }

  if (!user) {
    return (
      <SignInForm
        onSignedIn={(found) => {
          current = found;
          setUser(found);
        }}
      />
    );
  }

  if (user.mustChangePassword) {
    return (
      <ChangePasswordForm
        onChanged={() => {
          const next = { ...user, mustChangePassword: false };
          current = next;
          setUser(next);
        }}
      />
    );
  }

  return <>{children}</>;
};
